import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@app/core/services/auth.service';
import { SettingService } from '@app/core/services/models/setting.service';
import { APP_MENU_DEFINITION, AppMenuLeafDef } from '@app/core/constants/app-menu.definition';

/** Entrée menu : métadonnées `permission` retirées après filtrage (PrimeNG). */
type MenuNode = MenuItem & {
    permission?: string | string[];
    permissionMode?: 'all' | 'any';
    /** Nom du setting shop (`settings.name`) qui doit être actif pour afficher l'entrée. */
    requireSetting?: string;
    /** Visible uniquement pour les super-admins. */
    superAdminOnly?: boolean;
    /** Visible si l'utilisateur a au moins un des rôles listés. */
    roles?: string | string[];
};

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul> `
})
export class AppMenu implements OnInit {
    model: MenuItem[] = [];
    private readonly _sections = inject(APP_MENU_DEFINITION);

    constructor(
        private _authService: AuthService,
        private _settingService: SettingService,
    ) {}

    ngOnInit() {
        this._authService.authenticated();
        const apply = () => {
            const raw = this.buildRawMenu();
            this.model = this.filterMenu(raw);
        };
        if (this._settingService.settings?.length) {
            apply();
            return;
        }
        this._settingService.all({ skipLoader: true }).subscribe({
            next: (res: any) => {
                const list = Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
                this._settingService.setSettings(list ?? []);
                apply();
            },
            error: () => apply(),
        });
    }

    private buildRawMenu(): MenuNode[] {
        const sortedSections = this.sortIfOrderDefined([...this._sections]);
        return sortedSections.map((section) => ({
            label: section.label,
            icon: section.icon,
            requireSetting: section.requireSetting,
            items: this.sortIfOrderDefined([...section.items]).map((item) => this.leafToMenuNode(item)),
        }));
    }

    /**
     * Si aucun `order` n'est défini, on respecte l'ordre du tableau (utile pour patch custom via insert/splice).
     * Dès qu'au moins un élément a un `order`, on trie tout le tableau par `order`, puis `label`.
     */
    private sortIfOrderDefined<T extends { order?: number; label?: string }>(items: T[]): T[] {
        if (!items.some((x) => x.order !== undefined)) {
            return items;
        }
        return items.sort((a, b) => this.sortByOrderThenLabel(a, b));
    }

    private sortByOrderThenLabel(a: { order?: number; label?: string }, b: { order?: number; label?: string }): number {
        const ao = a.order ?? Number.POSITIVE_INFINITY;
        const bo = b.order ?? Number.POSITIVE_INFINITY;
        if (ao !== bo) return ao - bo;
        const al = a.label ?? '';
        const bl = b.label ?? '';
        return al.localeCompare(bl, 'fr', { sensitivity: 'base' });
    }

    /** Retire les champs réservés à l'éditeur de rôles avant affichage PrimeNG. */
    private leafToMenuNode(item: AppMenuLeafDef): MenuNode {
        const { actionPermissions: _a, roleEditorSkip: _r, ...rest } = item;
        return rest as MenuNode;
    }


    private filterMenu(nodes: MenuNode[]): MenuItem[] {
        return nodes.map((n) => this.filterBranch(n)).filter((x): x is MenuItem => x !== null);
    }

    private filterBranch(node: MenuNode): MenuItem | null {
        if (node.separator) {
            return node;
        }
        if (node.items?.length) {
            if (node['requireSetting'] && !this._settingService.isModuleEnabled(node['requireSetting'])) {
                return null;
            }
            const children = node.items
                .map((c) => this.filterBranch(c as MenuNode))
                .filter((x): x is MenuItem => x !== null);
            if (children.length === 0) {
                return null;
            }
            const { permission: _p, permissionMode: _m, requireSetting: _rs, superAdminOnly: _sa, roles: _ro, ...rest } = node;
            return { ...rest, items: children };
        }
        if (!this.canShow(node)) {
            return null;
        }
        const { permission: _p, permissionMode: _m, requireSetting: _rs, superAdminOnly: _sa, roles: _ro, ...rest } = node;
        return rest;
    }

    private canShow(node: MenuNode): boolean {
        if (node['requireSetting'] && !this._settingService.isModuleEnabled(node['requireSetting'])) {
            return false;
        }
        if (node['superAdminOnly']) {
            return this._authService.hasRole('super-admin');
        }
        if (node['roles'] !== undefined) {
            return this._authService.hasRole(node['roles'] as string | string[]);
        }
        if (node.permission === undefined) {
            return true;
        }
        return this._authService.hasPermission(node.permission, node.permissionMode ?? 'any');
    }
}
